"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCreateInQuery } from "@/lib/hooks/admin-dashboard/inquery/post/useCreateInquery";
import toast from "react-hot-toast";

const FormSchema = z.object({
  how: z.string().min(2, {
    message: "Username must be at least 2 characters.",
  }),
  name: z.string().min(2, {
    message: "Username must be at least 2 characters.",
  }),
  address: z.string().min(2, {
    message: "Username must be at least 2 characters.",
  }),
  phone: z.string().min(2, {
    message: "Username must be at least 2 characters.",
  }),
  descripton: z.string().min(2, {
    message: "Username must be at least 2 characters.",
  }),
});
interface WhyFormInterface {
  title?: string | null;
}
const WhyForm = ({ title }: WhyFormInterface) => {
  const { mutate: createInQuery } = useCreateInQuery();
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      address: "",
      phone: "",
      descripton: "",
    },
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    createInQuery({
      how: data.how,
      name: data.name,
      address: data.address,
      phone: data.phone,
      description: data.descripton,
    });
    form.reset();
    toast.success("Form submitted successfully");
  }

  return (
    <aside className="bg-zinc-50 rounded-lg p-2 py-10 sm:p-8 w-full max-w-[30rem] h-full flex flex-col gap-10">
      <h3 className="font-semibold text-3xl sm:text-lg sm:text-center">
        Get expert advice from our engineers
      </h3>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="w-full space-y-6"
        >
          {
            title ? (
              <FormField
                control={form.control}
                name="how"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="placeholder:text-xl text-xl h-[3rem]"
                        placeholder="Name * .."
                        {...field}
                        value={title}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <FormField
                control={form.control}
                name="how"
                render={({ field }) => (
                  <FormItem>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger
                          className="placeholder:text-xl text-xl h-[3rem]"
                          aria-controls="radix-:Rplludfb:"
                          aria-expanded="false"
                          aria-autocomplete="none"
                          dir="ltr"
                          data-state="closed"
                          data-placeholder=""
                          aria-describedby=":R1lludfb:-form-item-description"
                          aria-invalid="false"
                          aria-label="Select how we can help"
                        >
                          <SelectValue placeholder="How can we help ?*" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={"home-inspection"}>
                          Home Inspection
                        </SelectItem>
                        <SelectItem value={"construction"}>Construction</SelectItem>
                        <SelectItem value={"renovation"}>Renovation</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )
          }
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    className="placeholder:text-xl text-xl h-[3rem]"
                    placeholder="Name * .."
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    className="placeholder:text-xl text-xl h-[3rem]"
                    placeholder="Address "
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    className="placeholder:text-xl text-xl h-[3rem]"
                    placeholder="Phone *"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="descripton"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea rows={4} placeholder="Description *" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            aria-label="submit form"
            className="bg-skillsewa-secondary w-full py-6 mt-4 text-lg"
            type="submit"
          >
            Submit
          </Button>
        </form>
      </Form>
    </aside>
  );
};

export default WhyForm;
